<script setup>
import api from '@/lib/api'
import { useLoadingBar } from '@/composables/loadingBar'
import { useConfirmDialog } from '@/composables/confirmDialog'
import i18n from '@/i18n'
import { useNotify } from '@/composables/notify'
import Card from './Card.vue'
import VisitorInvitationStatus from './VisitorInvitationStatus.vue'
import { Trash2, Mail, UserCheck, Clock, LogOut, MapPin, CircleCheck } from 'lucide-vue'
import { computed, ref } from 'vue'
import VisitorSelector from './VisitorSelector.vue'

const props = defineProps({
  flexopusEventId: {
    required: true,
    type: Number,
  },
  visitors: {
    required: true,
    type: Array,
  },
  visitorBuildingCheck: {
    required: true,
    type: Boolean,
  },
})

const emit = defineEmits(['update', 'details'])

const { withLoadingBar } = useLoadingBar()
const { confirm } = useConfirmDialog()
const { success } = useNotify()

async function addInvitation (visitorIds) {
  if (!await confirm({
    text: i18n.t('Visitors.ConfirmAddVisitorInvitation'),
  })) {
    return
  }
  await withLoadingBar(async () => await api.createVisitorInvitation({
    event_id: props.flexopusEventId,
    visitor_ids: visitorIds,
  }), true)
  success(i18n.t('Success.InvitationCreated'))
  emit('update')
  invitationAddOpen.value = false
}

async function removeInvitation (visitor) {
  if (!await confirm({
    text: i18n.t('Visitors.ConfirmRemoveVisitorInvitation'),
    danger: true,
  })) {
    return
  }

  await withLoadingBar(async () => await api.deleteVisitorInvitation(visitor.invitation.id), true)
  emit('update')
  success(i18n.t('Success.InvitationDeleted'))
}

const visitorsWithInvitations = computed(() => {
  return props.visitors.filter(({ invitation }) => !!invitation)
})

const visitorsWithoutInvitations = computed(() => {
  return props.visitors.filter(({ invitation }) => !invitation)
})

const filteredVisitors = computed(() => {
  let re = null
  try {
    re = new RegExp(visitorSearch.value, 'i')
  } catch {}
  return filteredByStatus.value.filter(v => re && (re.test(v.name) || re.test(v.email)))
})

function toggleFilter (filterKey) {
  activeFilter.value = activeFilter.value === filterKey ? null : filterKey
}

const statCards = computed(() => {
  const invited = props.visitors.filter(({ invitation }) => !!invitation)
  const unverified = invited.filter(({ verified }) => !verified)
  const arrived = invited.filter(({ invitation }) => !!invitation.checkin_time && !invitation.checkout_time)
  const notArrived = invited.filter(({ invitation }) => !invitation.checkin_time)
  const left = invited.filter(({ invitation }) => !!invitation.checkout_time)

  return [
    unverified.length > 0
      ? {
          label: 'Unverified',
          key: 'unverified',
          icon: UserCheck,
          warning: true,
          value: unverified.length,
          total: invited.length,
        }
      : {
          label: 'All verified',
          key: 'unverified',
          icon: CircleCheck,
          success: true,
          value: invited.length,
        },
    notArrived.length > 0
      ? {
          label: 'Missing',
          key: 'notArrived',
          icon: Clock,
          warning: true,
          value: notArrived.length,
          total: invited.length,
        }
      : {
          label: 'All arrived',
          key: 'notArrived',
          icon: CircleCheck,
          success: true,
          value: invited.length,
        },
    arrived.length < invited.length
      ? {
          label: 'On site',
          key: 'arrived',
          icon: MapPin,
          value: arrived.length,
          total: invited.length,
        }
      : {
          label: 'All on site',
          key: 'arrived',
          icon: CircleCheck,
          success: true,
          value: invited.length,
        },
    left.length < invited.length
      ? {
          label: 'Left',
          key: 'left',
          icon: LogOut,
          value: left.length,
          total: invited.length,
        }
      : {
          label: 'All left',
          key: 'left',
          icon: CircleCheck,
          success: true,
          value: invited.length,
        },
  ]
})

const activeFilter = ref(null)

const filteredByStatus = computed(() => {
  let base = visitorsWithInvitations.value

  switch (activeFilter.value) {
    case 'unverified':
      base = base.filter(({ verified }) => !verified)
      break
    case 'notArrived':
      base = base.filter(({ invitation }) => !invitation.checkin_time)
      break
    case 'arrived':
      base = base.filter(({ invitation }) => !!invitation.checkin_time && !invitation.checkout_time)
      break
    case 'left':
      base = base.filter(({ invitation }) => !!invitation.checkout_time)
      break
  }

  return base
})

const invitationAddOpen = ref(false)
const visitorSearch = ref('')
</script>

<template>
  <div v-if="!invitationAddOpen">
    <h2 class="text-base font-medium mb-3">
      {{ $t('Visitors.VisitorInvitations') }}
    </h2>
    <div class="text-sm text-neutral-700 mb-4">
      {{ $t('Visitors.Hint') }}
    </div>
    <div class="flex items-center gap-4 text-neutral-gray-500 mb-3">
      <div class="divider grow" />
      <div class="text-sm">
        {{ $tc('Visitors.NumberOfExternalAttendees', visitors.length) }}
      </div>
    </div>
    <template
      v-if="visitors.length"
    >
      <div class="mb-4">
        <div
          v-if="visitorsWithInvitations.length"
          class="card !p-3 mb-2 w-full text-left"
        >
          <template v-if="visitorsWithoutInvitations.length > 0">
            <div class="flex items-center gap-1 mb-1">
              <Mail class="w-4 h-4 text-neutral-400" />
              <span class="text-xs text-neutral-500">{{ $t('Visitors.VisitorInvitations') }}</span>
            </div>
            <div class="text-base font-semibold text-amber-600 mb-1">
              {{ $tc('Visitors.NumberOfNotInvited', visitorsWithoutInvitations.length) }}
            </div>
            <div class="text-xs text-neutral-500 mb-3">
              {{ $tc('Visitors.NumberOfTotalInvited', visitorsWithInvitations.length, { total: visitors.length }) }}
            </div>
            <button
              class="btn btn-primary w-full"
              :disabled="!visitorBuildingCheck"
              @click="invitationAddOpen = true"
            >
              {{ $t('Visitors.SendInvitations') }}
            </button>
            <div
              v-if="!visitorBuildingCheck"
              class="text-xs text-neutral-500 mt-2"
            >
              {{ $t('Visitors.BuildingCheck') }}
            </div>
          </template>
          <template v-else>
            <div class="flex items-center gap-1 mb-1">
              <CircleCheck class="w-4 h-4 text-green-600" />
              <span class="text-xs text-green-600">{{ $t('Visitors.VisitorInvitations') }}</span>
            </div>
            <div class="text-base font-semibold text-green-600">
              {{ $tc('Visitors.AllInvited', visitors.length) }}
            </div>
          </template>
        </div>

        <VisitorSelector
          v-else
          :visitors="visitorsWithoutInvitations"
          simplified
          @add-invitation="addInvitation"
          @details="emit('details', $event)"
        />

        <div
          v-if="visitorsWithInvitations.length"
          class="grid grid-cols-2 gap-2"
        >
          <button
            v-for="card in statCards"
            :key="card.label"
            class="card !p-3 text-left transition-all"
            :class="{
              'card-active': activeFilter === card.key,
              'card-clickable': !card.success && card.value !== 0,
            }"
            :disabled="card.success || card.value === 0"
            @click="toggleFilter(card.key)"
          >
            <template v-if="!card.success">
              <div class="flex items-center gap-1 mb-1">
                <component
                  :is="card.icon"
                  class="w-4 h-4 text-neutral-400"
                />
                <span class="text-xs text-neutral-500">{{ card.label }}</span>
              </div>
              <div
                class="text-xl font-semibold"
                :class="card.warning ? 'text-amber-600' : 'text-neutral-600'"
              >
                {{ card.value }}<span class="text-sm text-neutral-400 font-normal">/{{ card.total }}</span>
              </div>
            </template>
            <template v-else>
              <div class="flex items-center gap-1 mb-1">
                <component
                  :is="card.icon"
                  class="w-4 h-4 text-green-600"
                />
                <span class="text-xs text-green-600">{{ card.label }}</span>
              </div>
              <div class="text-xl font-semibold text-green-600">
                {{ card.value }} <span class="text-sm text-neutral-400 font-normal">{{ $tc('Visitors.NVisitors') }}</span>
              </div>
            </template>
          </button>
        </div>
      </div>
      <template v-if="visitorsWithInvitations.length">
        <input
          v-model="visitorSearch"
          type="search"
          class="input w-full mb-3"
          :placeholder="$t('Visitors.FilterVisitors')"
        >
        <div class="flex items-center gap-4 text-neutral-gray-500 mb-4">
          <div class="divider grow" />
          <div class="text-sm">
            <template v-if="activeFilter || visitorSearch">
              {{ $tc('Visitors.Results', filteredVisitors.length) }}
              <button
                class="text-microsoft-blue-default hover:underline ml-2"
                @click="activeFilter = null; visitorSearch = ''"
              >
                {{ $t('Visitors.ClearFilters') }}
              </button>
            </template>
            <template v-else>
              {{ $tc('Visitors.NumberOfTotalInvited', visitorsWithInvitations.length, { total: visitors.length }) }}
            </template>
          </div>
        </div>
        <TransitionGroup
          tag="div"
          name="transition-list"
          class="flex flex-col gap-3 mb-4 relative"
        >
          <Card
            v-for="visitor in filteredVisitors"
            :key="visitor.id"
            selected
            :action="{
              label: $t('Visitors.Details.RemoveInvitation'),
              danger: true,
              icon: Trash2,
              onClick: () => removeInvitation(visitor)
            }"
            :subtitle="visitor.email"
            @open="emit('details', visitor)"
          >
            <template #title>
              {{ visitor.name }}
              <span
                v-if="!visitor.verified"
                v-tippy="$t('Visitors.Unverified')"
                class="inline-block text-white px-1.5 text-xs rounded font-medium text-center bg-amber-500 cursor-help"
              >?</span>
            </template>
            <template #details>
              <VisitorInvitationStatus :invitation="visitor.invitation" />
            </template>
          </Card>
        </TransitionGroup>
      </template>
    </template>
  </div>
  <VisitorSelector
    v-else
    class="fixed top-0 left-0 z-[100] bg-app-gradient w-screen h-screen p-4 flex flex-col min-h-auto"
    :visitors="visitorsWithoutInvitations"
    @add-invitation="addInvitation"
    @details="emit('details', $event)"
    @close="invitationAddOpen = false"
  />
</template>
